import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import { AUTHENTICATION_STATES } from '../../shared/constants/auth';
import { useAppContext } from '../../shared/contexts/AppContext';

/**
 * A component decorator that will require that the user has valid login credentials or the user
 * will be redirected to /login. Example usage:
 *
 * export default authRequired(MyExampleComponent);
 *
 * NOTE: The decorated component will still render even if the user has not yet authed, this happens
 * because auth happens asynchronously there is a period of time when we do not know whether or
 * not the user is authenticated.
 *
 * Your component should still check for the presence of
 * currentUser if it actually needs to use the user data.
 *
 * @param   {React.Component} DecoratedComponent The component that requires auth.
 * @returns {React.Component} A wrapper component that will perform the check.
 */
export default function authRequired(DecoratedComponent) {

	const UserAuthWrapper = (props) => {

		const { currentUser, authState, membership, myOrganization } = useAppContext();
		const { pathname, search } = useLocation();

		// Check if authentication failed, and redirect the user to the login page
		if (authState === AUTHENTICATION_STATES.FAILED) {

			const redirectParam = encodeURIComponent(`${pathname}${search}`);

			return <Redirect to={`/login?redirect=${redirectParam}`} />;

		}

		// Check if current user is authenticated
		if (authState === AUTHENTICATION_STATES.AUTHENTICATED && currentUser && myOrganization) {

			// Check if current user still needs to agree to the TechChange terms of service
			if (!currentUser.terms_of_use || !currentUser.privacy_policy) {

				// NOTE: prevent infinite looping
				if (pathname !== '/register/terms-of-service' && pathname.indexOf('/policies') !== 0) {

					const redirectParam = encodeURIComponent(`${pathname}${search}`);

					return <Redirect to={`/register/terms-of-service?redirect=${redirectParam}`} />;

				}

			}

			// Check if current user still needs to agree to the Organization's terms of service
			if (membership &&
				((myOrganization.terms_of_use_url && !membership.terms_of_use) ||
				(myOrganization.privacy_policy_url && !membership.privacy_policy))) {

				// NOTE: prevent infinite looping
				if (pathname !== '/register/terms-of-service' && pathname.indexOf('/policies') !== 0) {

					const redirectParam = encodeURIComponent(`${pathname}${search}`);

					return <Redirect to={`/register/terms-of-service?redirect=${redirectParam}`} />;

				}

			}

			// Otherwise, we can finally render the decorated component...
			return <DecoratedComponent {...props} />;

		}

		// Otherwise, the user authentication is still in progress...
		return <div></div>;

	};

	return UserAuthWrapper;

}
